import {
  isBrowserUploadSessionId,
  type PersistableUploadSession,
  type UploadSessionIdentity,
} from './upload-session-journal';

/**
 * The record kept in browser storage for each file a send took with it
 * (#4844), and the only ways one is built: from a write, a patch, or a row
 * read back. Each picks its fields one by one, so a multipart session keeps
 * its id, storage key, content identity and stored parts and never its
 * control URL or bearer token.
 */

export type PersistedUploadIdentity = UploadSessionIdentity;

export type PersistedMultipartState = PersistableUploadSession;

export type PersistedUpload = {
  uploadId: string;
  /**
   * The signed-in member who sent it. Only they resume it; absent only on
   * rows written before it was recorded, which nobody resumes.
   */
  actorId?: string;
  orgSlug: string;
  orgThreadId: string;
  /** The task's route id (`ot_…`), when the send knew it. */
  routeRef?: string;
  pendingSendId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  lastModified: number;
  kind: 'image' | 'file';
  referenceCategory?: string;
  multipart?: PersistedMultipartState;
  blobStore: 'opfs' | 'idb' | 'none';
  createdAt: number;
  updatedAt: number;
};

export type NewPersistedUpload = Omit<
  PersistedUpload,
  'blobStore' | 'createdAt' | 'updatedAt'
>;

export type PersistedUploadPatch = Partial<
  Omit<PersistedUpload, 'uploadId' | 'createdAt' | 'updatedAt' | 'multipart'>
> & { multipart?: Partial<PersistedMultipartState> };

function sortedParts(parts: ReadonlyArray<unknown>): number[] {
  return [...new Set(parts)]
    .filter(
      (part): part is number =>
        typeof part === 'number' && Number.isInteger(part) && part > 0
    )
    .sort((a, b) => a - b);
}

function identityOf(value: unknown): PersistedUploadIdentity | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const identity = value as Partial<PersistedUploadIdentity>;
  if (
    typeof identity.contentHash !== 'string' ||
    typeof identity.sizeBytes !== 'number' ||
    !Array.isArray(identity.partMd5s) ||
    !identity.partMd5s.every((md5) => typeof md5 === 'string')
  )
    return undefined;
  return {
    contentHash: identity.contentHash,
    sizeBytes: identity.sizeBytes,
    partMd5s: [...identity.partMd5s],
  };
}

/**
 * A multipart session as browser storage may keep it, or undefined. A row
 * from before #4844's fix kept the control URL instead of a session id:
 * that session is dropped, so the upload resumes in a fresh one.
 */
export function persistedMultipartOf(
  value: unknown
): PersistedMultipartState | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const session = value as Record<string, unknown>;
  if (
    !isBrowserUploadSessionId(session.sessionId) ||
    typeof session.storageKey !== 'string' ||
    !session.storageKey
  )
    return undefined;
  const identity = identityOf(session.identity);
  return {
    sessionId: session.sessionId,
    storageKey: session.storageKey,
    ...(identity ? { identity } : {}),
    completedParts: sortedParts(
      Array.isArray(session.completedParts) ? session.completedParts : []
    ),
  };
}

const optionalString = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

/**
 * A stored row as a record, or null when it is not one. Every field is
 * picked, so whatever else a row carries (an old control URL) is left out.
 */
export function persistedUploadOf(value: unknown): PersistedUpload | null {
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  if (
    typeof row.uploadId !== 'string' ||
    typeof row.orgSlug !== 'string' ||
    typeof row.orgThreadId !== 'string' ||
    typeof row.pendingSendId !== 'string' ||
    typeof row.filename !== 'string' ||
    typeof row.sizeBytes !== 'number' ||
    (row.blobStore !== 'opfs' &&
      row.blobStore !== 'idb' &&
      row.blobStore !== 'none')
  )
    return null;
  const actorId = optionalString(row.actorId);
  const routeRef = optionalString(row.routeRef);
  const referenceCategory = optionalString(row.referenceCategory);
  const multipart = persistedMultipartOf(row.multipart);
  return {
    uploadId: row.uploadId,
    ...(actorId ? { actorId } : {}),
    orgSlug: row.orgSlug,
    orgThreadId: row.orgThreadId,
    ...(routeRef ? { routeRef } : {}),
    pendingSendId: row.pendingSendId,
    filename: row.filename,
    mimeType: optionalString(row.mimeType) ?? '',
    sizeBytes: row.sizeBytes,
    lastModified: typeof row.lastModified === 'number' ? row.lastModified : 0,
    kind: row.kind === 'image' ? 'image' : 'file',
    ...(referenceCategory ? { referenceCategory } : {}),
    ...(multipart ? { multipart } : {}),
    blobStore: row.blobStore,
    createdAt: typeof row.createdAt === 'number' ? row.createdAt : 0,
    updatedAt: typeof row.updatedAt === 'number' ? row.updatedAt : 0,
  };
}

/**
 * Shallow-merge a patch. `multipart` merges too: completed parts accumulate
 * while the session stays the same, and a new session id (a fresh session)
 * starts over from the patch's parts.
 */
export function mergePersistedUpload(
  current: PersistedUpload,
  patch: PersistedUploadPatch,
  now: number
): PersistedUpload {
  const { multipart: multipartPatch, ...rest } = patch;
  const next: PersistedUpload = { ...current, ...rest, updatedAt: now };
  if (!multipartPatch) return next;
  const base = current.multipart;
  const sessionId = multipartPatch.sessionId ?? base?.sessionId;
  const storageKey = multipartPatch.storageKey ?? base?.storageKey;
  // A patch cannot start a session without saying which one.
  if (!sessionId || !storageKey) return next;
  const identity = multipartPatch.identity ?? base?.identity;
  const multipart = persistedMultipartOf({
    sessionId,
    storageKey,
    identity,
    completedParts: [
      ...(base && base.sessionId === sessionId ? base.completedParts : []),
      ...(multipartPatch.completedParts ?? []),
    ],
  });
  if (multipart) next.multipart = multipart;
  return next;
}
